/**
 * useBreadcrumbs Hook
 *
 * UX12: Auto-generates breadcrumb navigation from current route
 *
 * Features:
 * - Automatically parses URL path into breadcrumb segments
 * - Humanizes path segments (e.g., 'admin-projects' → 'Admin Projects')
 * - Supports custom breadcrumb overrides for specific pages
 * - Returns properly formatted breadcrumb items for Breadcrumb component
 *
 * @example
 * ```tsx
 * const breadcrumbs = useBreadcrumbs({
 *   customLabels: {
 *     '/admin/projects/123': 'Edit Project: My Awesome App'
 *   }
 * })
 * ```
 */

import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { BreadcrumbItem } from '@/components/ui/Breadcrumb'

export interface UseBreadcrumbsOptions {
  /** Custom labels for specific paths (overrides auto-generated labels) */
  customLabels?: Record<string, string>
  /** Root label (default: 'Home') */
  rootLabel?: string
  /** Root href (default: '/') */
  rootHref?: string
}

/**
 * Convert path segment to human-readable label
 * Examples:
 * - 'admin' → 'Admin'
 * - 'projects' → 'Projects'
 * - 'theme-controls' → 'Theme Controls'
 * - '123' → '123' (IDs stay as-is)
 */
function humanizeSegment(segment: string): string {
  // If segment is a number, return as-is (likely an ID)
  if (/^\d+$/.test(segment)) {
    return segment
  }

  // Replace hyphens/underscores with spaces, capitalize words
  return segment
    .split(/[-_]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Auto-generate breadcrumbs from current route
 */
export function useBreadcrumbs(options: UseBreadcrumbsOptions = {}): BreadcrumbItem[] {
  const { customLabels = {}, rootLabel = 'Home', rootHref = '/' } = options
  const location = useLocation()

  const breadcrumbs = useMemo(() => {
    const path = location.pathname
    const segments = path.split('/').filter(Boolean)

    // Start with root
    const items: BreadcrumbItem[] = [
      {
        label: rootLabel,
        href: rootHref,
      },
    ]

    // Build breadcrumbs from path segments
    let currentPath = ''
    segments.forEach((segment, index) => {
      currentPath += `/${segment}`
      const isLast = index === segments.length - 1

      // Check for custom label override
      const customLabel = customLabels[currentPath]

      items.push({
        label: customLabel || humanizeSegment(segment),
        href: isLast ? undefined : currentPath,
        current: isLast,
      })
    })

    return items
  }, [location.pathname, customLabels, rootLabel, rootHref])

  return breadcrumbs
}

/**
 * Admin-specific breadcrumbs hook with sensible defaults
 * Automatically sets root to 'Admin' and root href to '/admin'
 */
export function useAdminBreadcrumbs(
  customLabels: Record<string, string> = {}
): BreadcrumbItem[] {
  return useBreadcrumbs({
    rootLabel: 'Admin',
    rootHref: '/admin',
    customLabels,
  })
}
